"use client";
import React, { useState, useMemo, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import CalcInputValues from "../ui/CalcInputValues";
import { formatIndianNumber } from "@/utils/commonFunctions";
import { useIsMobile } from "@/utils/useIsMobile";
import CustomModal from "@/components/modal/CustomModal";
import Image from "next/image";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import StartSIPStrip from "../strips/StartSIPStrip";
import { useLoginModal } from "@/utils/useLoginModal";
import { allLumpsumCalculation } from "@/utils/allCalculators";
import { ROOT_URL } from "@/utils/gloableVariables";

type CalcKey = "Future Value" | "Target Amount" | "Duration" | "Return Rate";

type CalcId =
  | "futureValueCalc"
  | "targetValueCalc"
  | "knowDuration"
  | "knowRetRate";

function formatCompactNumber(num: number, decimalValue = false): string {
  const units = [
    { value: 10000000000, suffix: "B" },
    { value: 10000000, suffix: "Cr" },
    { value: 100000, suffix: "L" },
    { value: 1000, suffix: "k" },
  ];

  for (const { value, suffix } of units) {
    if (num >= value) {
      const formatted = num / value;
      return (
        (decimalValue ? formatted.toFixed(2) : formatted.toFixed(0)).replace(
          /\.0+$/,
          "",
        ) + suffix
      );
    }
  }

  return num.toString();
}

export default function LumpsumCalc() {
  const [isOpen, setIsOpen] = useState(false);
  const [amount, setAmount] = useState(10000);
  const [years, setYears] = useState(5);
  const [rate, setRate] = useState(15);
  const [target, setTarget] = useState(1000000);
  const [activeTab, setActiveTab] = useState("Future Value");

  const tableRef = useRef<HTMLTableElement>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const { isLoggedIn, open, setModalKey } = useLoginModal();

  const isTab = useIsMobile(1025);
  const isMob = useIsMobile(769);

  const handleLogin = () => {
    open();
    setModalKey("lumpsum-calculator");
  };

  const calculation = useMemo(() => {
    let id: CalcId = "futureValueCalc";
    if (activeTab === "Target Amount") id = "targetValueCalc";
    if (activeTab === "Duration") id = "knowDuration";
    if (activeTab === "Return Rate") id = "knowRetRate";

    return allLumpsumCalculation(amount, years, rate, target, id);
  }, [amount, years, rate, target, activeTab]);

  // Excel Download
  const handleExcelDownload = () => {
    if (!isLoggedIn) return handleLogin();

    // take the dynamic data
    const worksheet = XLSX.utils.json_to_sheet(
      calculation.yearlyReport.map((row) => ({
        Years: row.year,
        Investment: row.investment.toFixed(2),
        Profit: row.profit.toFixed(2),
        "Current Value": row.currentValue.toFixed(2),
      })),
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Lumpsum Report");
    XLSX.writeFile(workbook, "Lumpsum_Investment_Report.xlsx");
  };

  const handlePDFDownload = async () => {
    if (!isLoggedIn) return handleLogin();

    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "p" });
    const { pageSize } = pdf.internal;
    const pageWidth = pageSize.getWidth();
    const pageHeight = pageSize.getHeight();

    // Layout
    const layout = {
      marginX: 12,
      headerY: 12,
      titleY: 18,
      headerArea: 22,
      footer: 12,
      rowH: 10,
    };
    const usableWidth = pageWidth - layout.marginX * 2;

    // Headers + column widths
    const headers = ["Years", "Investment", "Profit", "Current Value"];

    const colWidths = [0.2, 0.3, 0.25, 0.25].map((p) => p * usableWidth);

    // Helpers
    const centerText = (text: string, x: number, y: number, opts = {}) =>
      pdf.text(text, x, y, { align: "center", baseline: "middle", ...opts });

    const drawHeader = (first = false) => {
      const y = layout.headerY;

      pdf
        .setFontSize(22)
        .setTextColor(202, 202, 202)
        .text("MySIPonline", layout.marginX, y);

      if (first) {
        const title = "Lumpsum Report";
        pdf
          .setFontSize(18)
          .setTextColor(0)
          .text(title, pageWidth / 2, y, { align: "center" });
      }
    };

    const drawTableHeader = (y: number) => {
      let x = layout.marginX;
      headers.forEach((h, i) => {
        pdf
          .setFontSize(11)
          .setTextColor(255, 255, 255)
          .setFillColor(16, 188, 157)
          .setDrawColor(217, 217, 217);

        pdf.rect(x, y, colWidths[i], layout.rowH, "FD"); // Fill + Border
        centerText(h, x + colWidths[i] / 2, y + layout.rowH / 2);
        x += colWidths[i];
      });
    };

    const drawRow = (y: number, values: (string | number)[]) => {
      let x = layout.marginX;
      pdf.setFontSize(10).setTextColor(99, 89, 90).setDrawColor(217, 217, 217);
      values.forEach((val, i) => {
        pdf.rect(x, y, colWidths[i], layout.rowH);
        centerText(String(val), x + colWidths[i] / 2, y + layout.rowH / 2);
        x += colWidths[i];
      });
    };

    const drawFooter = (pages: number) => {
      for (let p = 1; p <= pages; p++) {
        pdf.setPage(p);
        const footerY = pageHeight - 8;
        pdf
          .setFontSize(12)
          .setTextColor(0)
          .text(`Page ${p}/${pages}`, layout.marginX, footerY);

        const prefix = "Report by ";
        const w = pdf.getTextWidth(prefix);
        pdf.text(prefix, pageWidth - layout.marginX - w - 40, footerY);

        pdf
          .setTextColor(0, 102, 153)
          .textWithLink(
            "MySIPonline",
            pageWidth - layout.marginX - 38,
            footerY,
            { url: "https://mysiponline.com" },
          );
      }
    };

    // === Build PDF ===
    let y = layout.headerArea;
    drawHeader(true);
    drawTableHeader(y);
    y += layout.rowH;

    for (const row of calculation.yearlyReport ?? []) {
      const values = [
        row.year,
        formatIndianNumber(Number(row.investment).toFixed(0)),
        formatIndianNumber(Number(row.profit).toFixed(0)),
        formatIndianNumber(Number(row.currentValue).toFixed(0)),
      ];

      if (y + layout.rowH > pageHeight - layout.footer) {
        pdf.addPage();
        drawHeader();
        y = layout.headerArea + 4;
        drawTableHeader(y);
        y += layout.rowH;
      }

      drawRow(y, values);
      y += layout.rowH;
    }

    drawFooter(pdf.internal.pages.length - 1);
    pdf.save("Lumpsum_Report.pdf");
  };

  const handleScrollReport = () => {
    if (reportRef.current) {
      const headerOffset = 30;
      const elementPosition =
        reportRef.current.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const handleRightPart = () => {
    if (!isTab) return;
    setIsOpen(true);
  };

  const calculaterOptions = [
    "Future Value",
    "Target Amount",
    "Duration",
    "Return Rate",
  ];

  const calcArr: Record<
    CalcKey,
    {
      heading: string;
      startText?: string;
      endText?: string;
      value?: string;
      textColor?: string;
    }
  > = {
    "Future Value": {
      heading: "Invest Show",
    },
    "Target Amount": {
      heading: "Goal Show",
      startText: `You will need to invest`,
      endText: `to reach the target`,
      value: `₹${calculation.investment}`,
      textColor: "text-[#148200]",
    },
    Duration: {
      heading: "Duration Show",
      startText: `You will need to invest for`,
      endText: `to reach the target`,
      value: `${calculation.tenure} years`,
      textColor: calculation.tenure < 0 ? "text-[#f90d0d]" : "text-[#148200]",
    },
    "Return Rate": {
      heading: "Return Show",
      startText: `You will need an annual return rate of`,
      endText: `to reach the target`,
      value: `${calculation.annualizedReturn}% annualized return`,
      textColor:
        calculation.annualizedReturn < 0 ? "text-[#f90d0d]" : "text-[#148200]",
    },
  };

  const activeCalc = calcArr[activeTab as CalcKey];

  const displayedData = useMemo(() => {
    if (isLoggedIn) return calculation.yearlyReport ?? [];

    const realRows = (calculation.yearlyReport ?? []).slice(0, 2);

    const fakeCount = Math.max(5 - realRows.length, 0);

    const startYear = realRows.length > 0 ? realRows.length + 1 : 1;

    const fakeRows = Array.from({ length: fakeCount }, (_, i) => ({
      year: `${startYear + i}`,
      investment: 1000,
      profit: 1000,
      currentValue: 1000,
      isFake: true,
    }));

    return [...realRows, ...fakeRows];
  }, [isLoggedIn, calculation.yearlyReport]);

  return (
    <>
      {/* Hero Heading */}
      <section className="section px-3 sm:px-6 xl:px-0 mb-12 sm:mb-100">
        {/* Floating Calculator */}
        <div className="relative text-white text-center xs:bg-white xs:rounded-xl xs:shadow-lg sm:overflow-hidden xs:p-6">
          <div
            className={`flex flex-col lg:flex-row gap-6 items-center w-full bg-white text-black rounded-xl shadow-lg xs:rounded-none xs:shadow-none p-4 sm:p-0 ${activeTab === "Future Value" ? "lg:h-131.5" : ""}`}
          >
            {/* Inputs */}
            <div className="w-full lg:w-[40%] shrink-0">
              <h2 className="xs:text-lg sm:text-2xl lg:text-2.5xl font-semibold text-center text-primaryGreen mb-4">
                I Want To Know
              </h2>

              <div className="grid grid-cols-2 gap-3 xs:flex items-center justify-between xs:border border-[#c6d3ea] rounded-xl p-1 mb-6 xs:mb-16 xs:overflow-x-auto scrollbar-none">
                {calculaterOptions.map((option) => (
                  <button
                    key={option}
                    onClick={() => setActiveTab(option)}
                    className={`py-2.5 px-2 xs:flex-1 lg:flex-auto whitespace-nowrap shrink-0 rounded-lg text-xs sm:text-2sm sm:leading-5.5 border xs:border-none border-[#c6d3ea] leading-4 transition ${activeTab === option ? "bg-primaryGreen text-white font-medium" : "text-gray-700 hover:bg-gray-100"}`}
                  >
                    {option}
                  </button>
                ))}
              </div>

              <div className="space-y-8">
                {activeTab === "Future Value" && (
                  <>
                    <CalcInputValues
                      label="I want to invest"
                      min={1000}
                      max={200000000}
                      step={500}
                      unit="₹"
                      amountValue={amount}
                      showFloatingValue
                      unitPosition="left"
                      onChange={setAmount}
                    />
                    <CalcInputValues
                      label="Over a period of (Years)"
                      min={1}
                      max={50}
                      unit="Years"
                      amountValue={years}
                      onChange={setYears}
                    />
                    <CalcInputValues
                      label="Expected Rate of Return(%)"
                      min={1}
                      max={50}
                      unit="%"
                      allowDecimal
                      amountValue={rate}
                      onChange={setRate}
                    />
                  </>
                )}

                {activeTab === "Target Amount" && (
                  <>
                    <CalcInputValues
                      label="I need (Target Amount)"
                      min={1000}
                      max={200000000}
                      step={500}
                      unit="₹"
                      amountValue={target}
                      showFloatingValue
                      unitPosition="left"
                      onChange={setTarget}
                    />
                    <CalcInputValues
                      label="After a period of (Years)"
                      min={1}
                      max={60}
                      unit="Years"
                      amountValue={years}
                      onChange={setYears}
                    />
                    <CalcInputValues
                      label="Expected Rate of Return(%)"
                      min={1}
                      max={50}
                      unit="%"
                      allowDecimal
                      unitPosition="left"
                      amountValue={rate}
                      onChange={setRate}
                    />
                  </>
                )}

                {activeTab === "Duration" && (
                  <>
                    <CalcInputValues
                      label="I need"
                      min={1000000}
                      max={200000000}
                      step={500}
                      unit="₹"
                      showFloatingValue
                      unitPosition="left"
                      amountValue={amount}
                      onChange={setTarget}
                    />
                    <CalcInputValues
                      label="I want to invest (One Time)"
                      min={500}
                      max={10000000}
                      step={500}
                      unit="₹"
                      showFloatingValue
                      unitPosition="left"
                      amountValue={target}
                      onChange={setAmount}
                    />
                    <CalcInputValues
                      label="Expected Rate of Return(%)"
                      min={1}
                      max={50}
                      unit="%"
                      allowDecimal
                      unitPosition="left"
                      amountValue={rate}
                      onChange={setRate}
                    />
                  </>
                )}

                {activeTab === "Return Rate" && (
                  <>
                    <CalcInputValues
                      label="I need"
                      min={100000}
                      max={200000000}
                      step={500}
                      unit="₹"
                      showFloatingValue
                      unitPosition="left"
                      amountValue={amount}
                      onChange={setTarget}
                    />
                    <CalcInputValues
                      label="I want to invest"
                      min={5000}
                      max={25000000}
                      step={500}
                      unit="₹"
                      showFloatingValue
                      unitPosition="left"
                      amountValue={target}
                      onChange={setAmount}
                    />
                    <CalcInputValues
                      label="For a period of (Years)"
                      min={1}
                      max={50}
                      unit="Years"
                      amountValue={years}
                      onChange={setYears}
                    />
                  </>
                )}
              </div>

              <button
                onClick={handleRightPart}
                className="lg:hidden mt-4 w-full max-w-fit mx-auto bg-primaryGreen rounded-xl text-sm font-medium text-white px-8 xs:px-14 py-2 xs:py-2.5"
              >
                Calculate
              </button>
            </div>

            {!isTab && (
              <RightPart
                className="hidden lg:block"
                calculation={calculation}
                activeCalc={activeCalc}
              />
            )}
          </div>
          {!isMob && <StartSIPStrip className="hidden sm:flex" />}
        </div>
      </section>

      <section className="section mb-half-px sm:hidden">
        {isMob && <StartSIPStrip className="sm:hidden" />}
      </section>

      {/* Detailed Report (margin auto from calc height) */}
      <section ref={reportRef} className="section mb-half-px sm:mb-100">
        <div className="flex items-center gap-2 xxxs:gap-3 mb-3">
          <h2 className="text-lg sm:text-2xl lg:text-3xl font-medium">
            Detailed Report
          </h2>

          <span className="ml-auto text-sm sm:text-base text-[#004b6b]">
            Download
          </span>

          <button
            onClick={handleExcelDownload}
            aria-label="Excel File Download Button"
            className="border border-[#56565640] rounded-lg px-3 py-2 hover:border-[#56565680]"
          >
            <Image
              src={`${ROOT_URL}images/swp-calculator/excel.svg`}
              alt="strip-image"
              width={24}
              height={24}
            />
          </button>

          <button
            onClick={handlePDFDownload}
            aria-label="PDF Download Button"
            className="border border-[#56565640] rounded-lg px-3 py-2 hover:border-[#56565680]"
          >
            <Image
              src={`${ROOT_URL}images/swp-calculator/pdf.svg`}
              alt="strip-image"
              width={24}
              height={24}
            />
          </button>
        </div>

        <div className="rounded-2xl overflow-hidden">
          <div
            className={`${isLoggedIn ? "overflow-auto" : "overflow-hidden"} relative rounded-2xl sm:px-5 max-h-141 scrollbar-xs sm:scrollbar-md border-2 border-primaryBlack`}
          >
            <table ref={tableRef} className="w-full text-left border-collapse">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-none">
                  <th className="p-0 sticky left-0 ">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">
                      Years
                    </p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">
                      Investment
                    </p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">
                      Profit
                    </p>
                  </th>
                  <th className="p-0">
                    <p className="p-4 whitespace-nowrap text-2xs sm:text-2sm lg:text-xl sm:border-b-2 border-primaryBlack font-semibold text-primaryBlack text-center bg-[#f2f2f2] sm:bg-white">
                      Current Value
                    </p>
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white">
                {displayedData.map((row) => (
                  <tr
                    key={row.year}
                    className="border-b border-[#cacaca] border-dashed last:border-0"
                  >
                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack sticky left-0 bg-[#f2f2f2] sm:bg-white">
                      {row.year}
                    </td>
                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">
                      {formatIndianNumber(Number(row.investment).toFixed(0), {
                        prefix: "₹",
                      })}
                    </td>
                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">
                      {formatIndianNumber(Number(row.profit).toFixed(0), {
                        prefix: "₹",
                      })}
                    </td>
                    <td className="px-4 py-3 text-center text-xs sm:text-2sm text-primaryBlack">
                      {formatIndianNumber(Number(row.currentValue).toFixed(0), {
                        prefix: "₹",
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!isLoggedIn && (
              <div className="absolute inset-0 top-20 bottom-1 xs:bottom-0 xs:top-24 z-1 mx-auto max-w-[98%] bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,rgba(255,255,255,0.39)_41.78%)] flex items-center justify-center text-center text-sm sm:text-base font-medium text-primaryBlack px-6">
                <div className="gradient-blur rounded-2xl">
                  <div /> <div /> <div /> <div /> <div /> <div />
                </div>

                <div className="relative z-1 bg-white xs:w-4/5 xl:w-2/3 h-fit border border-[#e6e6e6] shadow-[0px_4px_4px_0px_#00000026] flex flex-col sm:flex-row items-center p-4 xxxs:p-5 md:pl-10 rounded-md overflow-hidden gap-2 sm:gap-0">
                  <Image
                    className="absolute z-0 top-0 left-0 bottom-0"
                    src={`${ROOT_URL}images/swp-calculator/curves.svg`}
                    alt="curves-image"
                    width={60}
                    height={50}
                  />
                  <p className="flex items-center gap-2 mx-auto xs:mx-0">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 28 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="relative z-1 w-4 h-4 xs:w-7 xs:h-7"
                    >
                      <rect width="28" height="28" rx="14" fill="#1A51AA" />
                      <path
                        d="M14.3334 11.3334C13.6258 11.3334 12.952 11.474 12.3334 11.7246V9.3334C12.3334 8.22918 13.2292 7.3334 14.3334 7.3334C15.4382 7.3334 16.3334 8.22918 16.3334 9.3334V10H17.6668V9.3334C17.6668 7.49219 16.1746 6 14.3334 6C12.4922 6 11 7.49219 11 9.3334V12.5072C9.78199 13.4844 9 14.983 9 16.6666C9 19.612 11.388 22 14.3334 22C17.2788 22 19.6668 19.612 19.6668 16.6666C19.6668 13.7212 17.2788 11.3334 14.3334 11.3334ZM15 17.1484V18.6666H13.6668V17.1484C13.2696 16.918 13 16.4922 13 16C13 15.2642 13.597 14.6666 14.3334 14.6666C15.0698 14.6666 15.6668 15.2642 15.6668 16C15.6668 16.4922 15.3972 16.918 15 17.1484Z"
                        fill="white"
                      />
                    </svg>

                    <span className="whitespace-nowrap relative z-1 text-sm xs:text-base sm:text-lg text-primaryBlue font-medium">
                      Sign in to Unlock Premium Details
                    </span>
                  </p>
                  <button
                    onClick={handleLogin}
                    className="bg-transparent border-2 text-2xs sm:text-base border-primaryBlue text-primaryBlue rounded-full font-medium mx-auto sm:mr-0 sm:ml-auto py-1 px-10 w-fit transition duration-300 hover:bg-[#CAE7FF]"
                  >
                    Unlock Now
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {isTab && (
        <CustomModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          maxSize="max-w-[80%]"
          useDragToClose
          isBlurOverlay
          className="bg-white rounded-b-none sm:rounded-4xl"
        >
          <RightPart
            className="block lg:hidden"
            calculation={calculation}
            activeCalc={activeCalc}
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            onScrollToReport={handleScrollReport}
          />
        </CustomModal>
      )}
    </>
  );
}

function RightPart({
  calculation,
  activeCalc,
  className,
  isOpen,
  onClose,
  onScrollToReport,
}: {
  isOpen?: boolean;
  className?: string;
  calculation: ReturnType<typeof allLumpsumCalculation>;
  activeCalc: {
    startText?: string;
    endText?: string;
    value?: string;
    heading: string;
    textColor?: string;
  };
  onClose?: () => void;
  onScrollToReport?: () => void;
}) {
  const isMob = useIsMobile();
  const [isChartLoading, setIsChartLoading] = useState(true);

  const chartOptions: Highcharts.Options = {
    chart: {
      type: "line",
      height: isMob ? 250 : 300,
      spacing: [24, 10, 10, 10],
      style: {
        fontFamily: "Inter, sans-serif", // global font
      },
      events: {
        load: () => {
          setIsChartLoading(false);
        },
      },
    },
    title: {
      text: undefined,
    },
    xAxis: {
      categories: calculation.yearlyReport.map((d) => d.year.toString()),
      title: {
        text: "Investment Period (Years)",
        style: {
          fontSize: "12px",
          fontWeight: "500",
          color: "#666",
        },
      },
      labels: {
        style: {
          fontSize: "12px",
          fontWeight: "500",
          color: "#333",
        },
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: "Value of Investment (₹)",
        style: {
          fontSize: "12px",
          fontWeight: "500",
          color: "#666",
        },
      },
      labels: {
        style: {
          fontSize: "12px",
          fontWeight: "500",
          color: "#333",
        },
        formatter: function () {
          return "₹" + formatCompactNumber(this.value as number, false);
        },
      },
    },
    tooltip: {
      useHTML: true,
      shared: true,
      formatter: function () {
        const invested = Math.round(this.points?.[0].y as number);
        const resultant = Math.round(this.points?.[1].y as number);
        return `
        <div class="bg-white text-center">
          <div class="font-bold">year ${this.key}</div>
          <div class="font-semibold text-[#8E44AD]"><span class="font-normal">Invested Amount:</span> ${formatIndianNumber(Number(invested), { prefix: "₹" })}</div>
          <div class="font-semibold text-[#16A085]"><span class="font-normal">Resultant Amount:</span> ${formatIndianNumber(Number(resultant), { prefix: "₹" })}</div>
        </div>
      `;
      },
    },
    credits: {
      enabled: false,
    },
    legend: {
      useHTML: true,
      labelFormatter: function () {
        const className =
          this.name === "Invested" ? "bg-[#8E44AD]" : "bg-[#16A085]";
        return `<div class="flex items-center gap-1"> 
                    <span class="w-3 h-3 inline-block rounded-full ${className}"></span> ${this.name}
                </div>`;
      },
      symbolHeight: 0,
      symbolWidth: 0,
      symbolRadius: 0,
      align: "right",
      verticalAlign: "top",
      layout: "horizontal",
      x: -10,
      y: 0,
    },
    series: [
      {
        type: "line",
        name: "Invested",
        data: calculation.yearlyReport.map((d) => d.investment),
        color: "#8E44AD",
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 6,
            },
          },
        },
      },
      {
        type: "line",
        name: "Resultant",
        data: calculation.yearlyReport.map((d) => Math.round(d.currentValue)),
        color: "#16A085",
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 6,
            },
          },
        },
      },
    ],
  };

  return (
    <>
      <div
        className={`w-full lg:w-[58%] lg:border-2 border-[#006b6633] rounded-2xl p-5 pt-0 sm:p-6 sm:pt-0 lg:p-6 bg-white sm:bg-[#f9fbfb] ${className}`}
      >
        <div className="h-250 xs:h-300">
          {isChartLoading && (
            <Image
              src="/next_assets/images/gif/chart_loading.gif"
              unoptimized
              alt="Loading Chart Animation"
              loading="eager"
              width={256}
              height={144}
              priority
              className="w-64 h-36 mx-auto mt-14"
            />
          )}
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </div>

        <div className="bg-white px-0 sm:p-4 rounded-2xl mt-4 mx-auto sm:w-[90%] space-y-6">
          {activeCalc.startText && (
            <p className="text-[19px] font-semibold text-primaryBlack">
              {activeCalc.startText}{" "}
              <span className={activeCalc.textColor}>{activeCalc.value} </span>
              {activeCalc.endText}
            </p>
          )}
          <div className="flex items-center justify-between text-2xs xs:text-sm sm:text-base text-[#222222] font-medium">
            <p>Future Expected Value</p>
            <p>
              {formatIndianNumber(Number(calculation.futureValue), {
                prefix: "₹",
              })}{" "}
              <span className="text-[#808080]">
                ({formatCompactNumber(calculation.futureValue, true)})
              </span>
            </p>
          </div>
          <div className="flex items-center justify-between text-2xs xs:text-sm sm:text-base text-[#222222] font-medium">
            <p>Investment Amount</p>
            <p>
              {formatIndianNumber(Number(calculation.investment), {
                prefix: "₹",
              })}{" "}
              <span className="text-[#808080]">
                ({formatCompactNumber(calculation.investment, true)})
              </span>
            </p>
          </div>
          <div className="flex items-center justify-between text-2xs xs:text-sm sm:text-base text-[#222222] font-medium">
            <p>Total Return Earned</p>
            <p>
              {formatIndianNumber(Number(calculation.absProfitValue), {
                prefix: "₹",
              })}{" "}
              <span className="text-[#808080]">
                ({formatCompactNumber(calculation.absProfitValue, true)})
              </span>
            </p>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="lg:hidden sticky bottom-0 flex items-center rounded-b-2xl justify-around gap-3 py-3 sm:py-5 bg-white border-t border-[#e4e4e4]">
          <a
            href={`${ROOT_URL}start-sip.php`}
            className="w-full max-w-fit mx-auto bg-primaryGreen rounded-xl border border-primaryGreen text-sm font-medium text-white px-5 xs:px-14 py-2 xs:py-2.5"
          >
            Start Lumpsum
          </a>
          <button
            onClick={() => {
              onClose?.();
              setTimeout(() => onScrollToReport?.(), 300);
            }}
            className="w-full max-w-fit mx-auto bg-white text-primaryGreen rounded-xl text-sm font-medium border border-primaryGreen px-5 xs:px-14 py-2 xs:py-2.5"
          >
            View Detail Report
          </button>
        </div>
      )}
    </>
  );
}
